import React from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import './InfoPanel.css';
import { projectMeta } from '../projects/data';
import { useScramble } from 'use-scramble';

const toItem = (id) => ({
  title: projectMeta[id]?.title || id,
  date: projectMeta[id]?.date || '',
  description: projectMeta[id]?.description || ''
});
const ITEMS = {
  marketplace: toItem('marketplace'),
  aiproxy: toItem('aiproxy'),
  golfgoals: toItem('golfgoals'),
  letterfall: toItem('letterfall'),
  abc: toItem('abc'),
  swiftui: toItem('swiftui'),
  facebook: toItem('facebook'),
  joypad: toItem('joypad'),
  figma: toItem('figma'),
  archive: toItem('archive')
};

export default function InfoPanel({ activeId }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const content = activeId ? ITEMS[activeId] : null;

  // Scramble: title, date, description
  const { ref: titleRef, replay: replayTitle } = useScramble({
    text: content?.title || '',
    speed: 0.8,
    tick: 1,
    step: 1,
    scramble: 6,
    seed: 1,
    playOnMount: false
  });
  const { ref: dateRef, replay: replayDate } = useScramble({
    text: content?.date || '',
    speed: 0.7,
    tick: 1,
    step: 1,
    scramble: 5,
    seed: 2,
    playOnMount: false
  });
  const { ref: descRef, replay: replayDesc } = useScramble({
    text: content?.description || '',
    speed: 0.65,
    tick: 1,
    step: 1,
    scramble: 7,
    seed: 3,
    playOnMount: false
  });

  React.useEffect(() => {
    function onMove(e) {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      mx.set(x);
      my.set(-y);
    }
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mx, my]);

  const rotateY = useTransform(mx, [-1, 1], [8, -8]);
  const rotateX = useTransform(my, [-1, 1], [-6, 6]);
  const translateX = useTransform(mx, [-1, 1], [-12, 12]);
  const translateY = useTransform(my, [-1, 1], [-12, 12]);

  // Only trigger scramble when the ID changes, not on every render
  const lastActiveIdRef = React.useRef(null);
  const replayTitleRef = React.useRef(replayTitle);
  const replayDateRef = React.useRef(replayDate);
  const replayDescRef = React.useRef(replayDesc);
  React.useEffect(() => { replayTitleRef.current = replayTitle; }, [replayTitle]);
  React.useEffect(() => { replayDateRef.current = replayDate; }, [replayDate]);
  React.useEffect(() => { replayDescRef.current = replayDesc; }, [replayDesc]);
  React.useEffect(() => {
    if (!content) return;
    if (lastActiveIdRef.current === activeId) return;
    lastActiveIdRef.current = activeId;
    replayTitleRef.current();
    const t1 = setTimeout(() => replayDateRef.current(), 60);
    const t2 = setTimeout(() => replayDescRef.current(), 120);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [activeId, content]);

  return (
    <motion.div
      className="info-panel"
      style={{ rotateX, rotateY, x: translateX, y: translateY }}
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: content ? 1 : 0, scale: content ? 1 : 0.98 }}
      transition={{ type: 'spring', stiffness: 180, damping: 20 }}
    >
      <div className="info-card" aria-hidden={!content}>
        <h3 className="info-title" ref={titleRef}>
          <span className="sr-only">{content?.title || ''}</span>
        </h3>
        <p className="info-meta"><span className="info-date" ref={dateRef} aria-label={content?.date || ''}></span></p>
        <p className="info-desc" ref={descRef} aria-label={content?.description || ''}></p>
      </div>
    </motion.div>
  );
}

// A 3D HUD variant that renders inside the Canvas and follows the camera
export function InfoPanel3D({ activeId, followObject = null, position = [0.8, 0.5, -1.5], offset = [0, 0, 0], tiltStrength = 0 }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const camRX = useMotionValue(0);
  const camRY = useMotionValue(0);
  const camRZ = useMotionValue(0);
  const groupRef = React.useRef();
  const tmpRefs = React.useMemo(() => ({
    pos: new THREE.Vector3(),
    quat: new THREE.Quaternion(),
    scale: new THREE.Vector3(),
    euler: new THREE.Euler(0, 0, 0, 'XYZ'),
    right: new THREE.Vector3(),
    up: new THREE.Vector3(),
    fwd: new THREE.Vector3()
  }), []);

  // Follow a 3D Object3D if provided (world transform), otherwise fall back to static position
  useFrame(() => {
    if (!groupRef.current) return;
    if (followObject && followObject.isObject3D) {
      // Copy world transform from the anchor
      followObject.updateWorldMatrix(true, false);
      const { pos, quat, scale, euler, right, up, fwd } = tmpRefs;
      followObject.matrixWorld.decompose(pos, quat, scale);

      // Apply local-space offset (Right/Up/Forward) in world space
      if (Array.isArray(offset)) {
        right.set(1, 0, 0).applyQuaternion(quat);
        up.set(0, 1, 0).applyQuaternion(quat);
        fwd.set(0, 0, 1).applyQuaternion(quat);
        pos.addScaledVector(right, offset[0] || 0);
        pos.addScaledVector(up, offset[1] || 0);
        pos.addScaledVector(fwd, offset[2] || 0);
      }

      groupRef.current.position.copy(pos);

      // Level the panel: remove roll so it doesn't tilt right/left
      euler.setFromQuaternion(quat, 'XYZ');
      euler.z = 0; // zero roll
      groupRef.current.quaternion.setFromEuler(euler);
      // Keep scale around 1 for legibility regardless of scene scale
      groupRef.current.scale.setScalar(1);
    } else {
      // Default fallback position if no anchor available
      if (Array.isArray(position)) {
        groupRef.current.position.set(position[0], position[1], position[2]);
      }
    }
  });

  React.useEffect(() => {
    function onMove(e) {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      mx.set(x);
      my.set(-y);
    }
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mx, my]);

  const rotateYMouse = useTransform(mx, [-1, 1], [6, -6]);
  const rotateXMouse = useTransform(my, [-1, 1], [-4, 4]);
  const rotateZMouse = useTransform(mx, [-1, 1], [-1, 1]);

  // Combine camera-driven tilt with mouse parallax for subtle integration
  const rotateY = useTransform([rotateYMouse, camRY], ([m, c]) => (m + c) * tiltStrength);
  const rotateX = useTransform([rotateXMouse, camRX], ([m, c]) => (m + c) * tiltStrength);
  const rotateZ = useTransform([rotateZMouse, camRZ], ([m, c]) => (m + c) * tiltStrength);

  // Drive cam-based tilt from camera Euler (yaw/pitch) each frame
  useFrame((state) => {
    const { camera } = state;
    const toDeg = (r) => r * 180 / Math.PI;
    // Use camera Euler; clamp to small UI-friendly tilt
    const yawDeg = toDeg(camera.rotation.y);     // left/right
    const pitchDeg = toDeg(camera.rotation.x);   // up/down
    // Increase sensitivity and add diagonal (roll-like) response
    const targetRY = Math.max(-8, Math.min(8, yawDeg * 0.35));
    const targetRX = Math.max(-6, Math.min(6, -pitchDeg * 0.3));
    const targetRZ = Math.max(-6, Math.min(6, yawDeg * 0.18 + (-pitchDeg) * 0.12));
    // Smooth damp
    const lerp = (a, b, t) => a + (b - a) * t;
    camRY.set(lerp(camRY.get(), targetRY, 0.06));
    camRX.set(lerp(camRX.get(), targetRX, 0.06));
    camRZ.set(lerp(camRZ.get(), targetRZ, 0.06));
  });
  const translateX = useTransform(mx, [-1, 1], [-8, 8]);
  const translateY = useTransform(my, [-1, 1], [-8, 8]);

  const content = activeId ? ITEMS[activeId] : null;

  // Scramble for in-scene HUD as well
  const { ref: titleRef3D, replay: replayTitle3D } = useScramble({
    text: content?.title || '',
    speed: 1.0,
    tick: 1,
    step: 10,
    overflow: true,
    scramble: 6,
    seed: 0,
    chance: 0.0,
    playOnMount: false
  });
  const { ref: dateRef3D, replay: replayDate3D } = useScramble({
    text: content?.date || '',
    speed: 1.0,
    tick: 1,
    step: 10,
    overflow: true,
    scramble: 5,
    seed: 0,
    chance: 0.0,
    playOnMount: false
  });
  const { ref: descRef3D, replay: replayDesc3D } = useScramble({
    text: content?.description || '',
    speed: 1.0,
    tick: 1,
    step: 10,
    overflow: true,
    scramble: 7,
    seed: 0,
    chance: 0.0,
    playOnMount: false
  });

  const lastActiveId3DRef = React.useRef(null);
  const replayTitle3DRef = React.useRef(replayTitle3D);
  const replayDate3DRef = React.useRef(replayDate3D);
  const replayDesc3DRef = React.useRef(replayDesc3D);
  React.useEffect(() => { replayTitle3DRef.current = replayTitle3D; }, [replayTitle3D]);
  React.useEffect(() => { replayDate3DRef.current = replayDate3D; }, [replayDate3D]);
  React.useEffect(() => { replayDesc3DRef.current = replayDesc3D; }, [replayDesc3D]);
  React.useEffect(() => {
    if (!content) return;
    if (lastActiveId3DRef.current === activeId) return;
    lastActiveId3DRef.current = activeId;
    replayTitle3DRef.current();
    const t1 = setTimeout(() => replayDate3DRef.current(), 60);
    const t2 = setTimeout(() => replayDesc3DRef.current(), 120);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [activeId, content]);

  return (
    <group ref={groupRef}>
      <Html
        transform
        sprite={false}
        occlude={false}
        center
        distanceFactor={1}
        portal={{ current: null }}
        style={{ pointerEvents: 'none' }}
      >
        <motion.div
          className="info-panel info-panel--3d"
          style={{ rotateX, rotateY, rotateZ, x: translateX, y: translateY }}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: content ? 1 : 0, scale: content ? 1 : 0.98 }}
          transition={{ type: 'spring', stiffness: 180, damping: 20 }}
        >
          <div className="info-card" aria-hidden={!content}>
            <h3 className="info-title" ref={titleRef3D}>
              <span className="sr-only">{content?.title || ''}</span>
            </h3>
            <p className="info-meta">
              <span className="info-date" ref={dateRef3D} aria-label={content?.date || ''}></span>
            </p>
            <p className="info-desc" ref={descRef3D} aria-label={content?.description || ''}></p>
          </div>
        </motion.div>
      </Html>
    </group>
  );
}


